import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { preserveNextInPath, resolvePostLoginPath } from '../utils/authRedirect';
import { authService } from '../api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { ArrowRight, Smartphone, RefreshCw } from 'lucide-react';

const inputStyle = {
    padding: '14px 16px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-bg-primary)',
    fontSize: '1rem',
    outline: 'none',
    width: '100%',
    transition: 'border-color var(--transition-fast)',
                  color: 'var(--color-text-primary)'
};

function LoginOTP() {
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    const handleSendOTP = async () => {
        if (!email) return toast.error('Please enter your email first');
        setSending(true);
        try {
            await authService.sendOTP(email);
            setOtpSent(true);
            toast.success('OTP sent to your email!');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to send OTP');
        } finally {
            setSending(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await authService.loginWithOTP(email, otp);
            // loginWithOTP returns token — we then fetch user
            const userRes = await authService.getCurrentUser();
            localStorage.setItem('user', JSON.stringify(userRes.data));
            toast.success('Logged in successfully!');
            navigate(resolvePostLoginPath(searchParams, location.state?.from), { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || err.response?.data?.error || 'Login failed. Check your OTP.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex" style={{ minHeight: '100vh', width: '100%', backgroundColor: 'var(--color-bg-primary)',
                  color: 'var(--color-text-primary)' }}>
            <div className="flex items-center justify-center" style={{ flex: 1, padding: '3rem 2rem', backgroundColor: 'var(--color-card-bg)',
                  color: 'var(--color-text-primary)' }}>
                <div style={{ width: '100%', maxWidth: '440px' }}>
                    <Link to="/" className="flex items-center gap-xs" style={{ marginBottom: '2.5rem', textDecoration: 'none' }}>
                        <div style={{
                            background: 'var(--gradient-brand)',
                            color: 'white', width: '36px', height: '36px', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', borderRadius: '10px',
                            fontWeight: 800, fontSize: '1.4rem',
                        }}>L</div>
                        <span style={{ fontWeight: 800, fontSize: '1.6rem', color: 'var(--color-text-primary)', letterSpacing: '-0.5px' }}>Learnly</span>
                    </Link>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <Smartphone size={28} color="var(--color-primary)" />
                        <h1 style={{ fontSize: '2.2rem', lineHeight: 1.2 }}>Login with OTP</h1>
                    </div>
                    <p className="text-muted" style={{ fontSize: '1rem', marginBottom: '2rem' }}>
                        We'll send a one-time code to your email address.
                    </p>

                    <div style={{
                        background: 'rgba(47,124,123,0.07)', border: '1px solid rgba(47,124,123,0.2)',
                        borderRadius: 'var(--radius-md)', padding: '0.85rem 1rem', marginBottom: '1.75rem',
                        fontSize: '0.9rem', color: 'var(--color-text-secondary)'
                    }}>
                        ℹ️ New accounts require admin approval before you can log in. You'll be notified by email once approved.
                    </div>

                    <form className="flex flex-col gap-lg" onSubmit={handleSubmit}>
                        <div className="flex flex-col gap-xs">
                            <label style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>Email Address</label>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <input
                                    type="email" required value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    style={inputStyle}
                                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                />
                                <button
                                    type="button" onClick={handleSendOTP} disabled={sending}
                                    className="btn btn-outline"
                                    style={{ whiteSpace: 'nowrap', padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)' }}
                                >
                                    {sending ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : otpSent ? 'Resend' : 'Send OTP'}
                                </button>
                            </div>
                        </div>

                        {otpSent && (
                            <div className="flex flex-col gap-xs">
                                <label style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>One-Time Password</label>
                                <input
                                    type="text" required value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    placeholder="Enter 6-digit code"
                                    maxLength={6}
                                    style={{ ...inputStyle, letterSpacing: '0.3rem', fontSize: '1.3rem', textAlign: 'center' }}
                                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                />
                            </div>
                        )}

                        {otpSent && (
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn btn-primary shadow-md flex items-center justify-center gap-sm w-full"
                                style={{ padding: '16px', fontSize: '1.1rem' }}
                            >
                                {loading ? 'Signing In...' : <> Verify & Sign In <ArrowRight size={20} /></>}
                            </button>
                        )}
                    </form>

                    <div style={{ borderTop: '1px solid var(--color-border)', marginTop: '1.75rem', paddingTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center' }}>
                        <p className="text-muted" style={{ fontSize: '0.9rem' }}>Or use another method</p>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <Link to={preserveNextInPath('/auth/login', searchParams)} className="btn btn-outline" style={{ padding: '0.6rem 1.25rem', fontSize: '0.9rem' }}>Password Login</Link>
                            <Link to="/auth/verify-email" className="btn btn-outline" style={{ padding: '0.6rem 1.25rem', fontSize: '0.9rem' }}>Email Verification</Link>
                        </div>
                        <p className="text-muted" style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                            Don't have an account? <Link to="/auth/register" style={{ color: 'var(--color-primary)', fontWeight: 600, textDecoration: 'none' }}>Sign up</Link>
                        </p>
                    </div>
                </div>
            </div>

            <div className="auth-photo-rail">
                <aside className="auth-photo-panel" aria-label="Passwordless login">
                    <img
                        className="auth-photo-panel__media"
                        src="/auth_panel.webp"
                        alt="Desk with notebook, books, and laptop by a window"
                        loading="eager"
                        decoding="async"
                        fetchpriority="high"
                        width="800"
                        height="1000"
                    />
                    <div className="auth-photo-panel__scrub" aria-hidden="true" />
                    <div className="auth-photo-panel__copy">
                        <span className="auth-photo-panel__kicker">Jalusi Learnly</span>
                        <h2>Passwordless, secure login</h2>
                        <p>
                            No passwords to remember. We send a one-time code straight to your inbox — fast, safe, simple.
                        </p>
                    </div>
                </aside>
            </div>
        </div>
    );
}

export default LoginOTP;
